import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiChevronLeft } from "react-icons/fi";
import { useGetProductQuery, useGetProductRecommendationsQuery } from "../../store/services/productService";
import Nav from "../../components/home/Nav";
import DetailsCard from "../../components/home/DetailsCard";
import ProductLoader from "../../components/home/ProductLoader";
import ProductRecommendations from "../../components/home/ProductRecommendations";
import { categoryUrl } from "../../utils/categoryUrl";
import { trackAnalytics } from "../../utils/analytics";
import Money from "../../components/ui/Money";
import { toPersianNumber } from "../../utils/persian";


const padTimer = (value) => String(Math.max(0, value)).padStart(2, "0");

const getCampaignCountdown = (endsAt) => {
  if (!endsAt) return null;

  const end = new Date(String(endsAt).replace(" ", "T"));
  const now = new Date();

  if (Number.isNaN(end.getTime())) return null;

  const diff = Math.max(0, end.getTime() - now.getTime());
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
    finished: diff <= 0
  };
};


const salePriceFor = (item) => {
  const basePrice = Number(item?.price || 0);
  const campaignPrice = Number(item?.campaign_sale_price || item?.temporary_sale_price || 0);
  const salePrice = Number(item?.sale_price || 0);
  const percent = Number(item?.discount || item?.discount_percent || item?.temporary_discount_percent || 0);

  if (campaignPrice > 0) return campaignPrice;
  if (salePrice > 0) return salePrice;
  if (percent > 0 && basePrice > 0) return Math.round(basePrice * (1 - Math.min(percent, 95) / 100));
  return basePrice;
};

const Product = () => {
  const { id, name } = useParams();
  const productRouteId = id || name;
  const { data, isFetching } = useGetProductQuery(productRouteId);
  const product = data?.product || data;
  const productId = product?.id || product?._id || productRouteId;
  const [specialSale, setSpecialSale] = useState(null);
  const [countdown, setCountdown] = useState(null);

  const { data: recommendationsData } = useGetProductRecommendationsQuery(productId, {
    skip: !productId || isFetching,
  });

  useEffect(() => {
    if (!productId || isFetching) return;

    let cancelled = false;

    fetch("/api/special-sale/current")
      .then((res) => res.ok ? res.json() : null)
      .then((res) => {
        if (cancelled || !res?.campaign || !Array.isArray(res.products)) return;

        const found = res.products.find((item) => String(item.id || item._id) === String(productId));

        if (found) {
          setSpecialSale({
            campaign: res.campaign,
            product: found
          });
        } else {
          setSpecialSale(null);
        }
      })
      .catch(() => {
        if (!cancelled) setSpecialSale(null);
      });

    return () => {
      cancelled = true;
    };
  }, [productId, isFetching]);

  const displayProduct = useMemo(() => {
    if (!product) return product;

    if (!specialSale?.product) return product;

    const campaignProduct = specialSale.product;
    const campaignSalePrice = Number(campaignProduct.campaign_sale_price || campaignProduct.sale_price || 0);
    const basePrice = Number(product.price || campaignProduct.price || 0);
    const discountPercent = basePrice > 0 && campaignSalePrice > 0 && campaignSalePrice < basePrice
      ? Math.round((1 - campaignSalePrice / basePrice) * 100)
      : Number(campaignProduct.discount || campaignProduct.discount_percent || product.discount_percent || 0);

    return {
      ...product,
      campaign_sale_price: campaignSalePrice || null,
      sale_price: campaignSalePrice || product.sale_price,
      discount: discountPercent,
      discount_percent: discountPercent,
      is_campaign_sale: true,
      special_sale_campaign: specialSale.campaign
    };
  }, [product, specialSale]);

  const isCampaignSale = Boolean(specialSale?.product);
  const campaignFinalPrice = isCampaignSale ? salePriceFor(displayProduct) : 0;
  const campaignBasePrice = Number(displayProduct?.price || 0);
  const campaignDiscount = campaignBasePrice > 0 && campaignFinalPrice > 0 && campaignFinalPrice < campaignBasePrice
    ? Math.round((1 - campaignFinalPrice / campaignBasePrice) * 100)
    : 0;

  useEffect(() => {
    if (!specialSale?.campaign?.ends_at) {
      setCountdown(null);
      return;
    }

    const update = () => {
      setCountdown(getCampaignCountdown(specialSale.campaign.ends_at));
    };

    update();

    const timer = setInterval(update, 1000);

    return () => clearInterval(timer);
  }, [specialSale?.campaign?.ends_at]);

  useEffect(() => {
    if (!isFetching && product?.id) {
      trackAnalytics("product_view", {
        product_id: product.id,
        category_id: product.category_id || null,
        metadata: {
          product_name: product.title || product.name || "",
          category: product.category || "",
          is_campaign_sale: isCampaignSale
        }
      });
    }
  }, [isFetching, product?.id, product?.category_id, product?.title, product?.name, product?.category, isCampaignSale]);

  return (
    <>
      <Nav />

      <main className={`product-detail-page ${isCampaignSale ? "product-detail-page-campaign-sale" : ""}`}>
        <div className="site-container">
          {isFetching ? (
            <ProductLoader />
          ) : product ? (
            <>
              <nav className="product-breadcrumb">
                <Link to="/">خانه</Link>
                <FiChevronLeft size={17} />
                <Link to={categoryUrl(product.category_slug || product.category, 1)}>{product.category}</Link>
                <FiChevronLeft size={17} />
                <span>{product.title || product.name}</span>
              </nav>

              {isCampaignSale && (
                <section className="product-campaign-detail-banner">
                  <div className="product-campaign-detail-title">
                    <span>فروش شگفت‌انگیز</span>
                    <strong>{specialSale?.campaign?.title || "فروش ویژه"}</strong>
                  </div>

                  <div className="product-campaign-detail-meta">
                    {campaignDiscount > 0 && <em>{toPersianNumber(campaignDiscount)}٪ تخفیف</em>}

                    {countdown && !countdown.finished && (
                      <div className="product-campaign-detail-timer">
                        {countdown.days > 0 && <b>{toPersianNumber(countdown.days)} روز</b>}
                        <b>{toPersianNumber(padTimer(countdown.hours))}</b>
                        <i>:</i>
                        <b>{toPersianNumber(padTimer(countdown.minutes))}</b>
                        <i>:</i>
                        <b>{toPersianNumber(padTimer(countdown.seconds))}</b>
                      </div>
                    )}
                  </div>

                  <div className="product-campaign-detail-price">
                    <b><Money value={campaignFinalPrice} /></b>
                    {campaignBasePrice > campaignFinalPrice && <del><Money value={campaignBasePrice} /></del>}
                  </div>
                </section>
              )}

              <DetailsCard product={displayProduct} />

              <ProductRecommendations
                title={recommendationsData?.title}
                products={recommendationsData?.products || []}
              />
            </>
          ) : (
            <div className="shop-empty-state">
              <strong>محصول پیدا نشد</strong>
              <p>ممکن است محصول حذف شده باشد یا آدرس وارد شده درست نباشد.</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default Product;
